import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileText, FilePlus, Users, FileCheck, Receipt, AlertTriangle,
  LogOut, Bell,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const nav = [
  { to: "/facility", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/facility/requests", icon: FileText, label: "My Requests" },
  { to: "/facility/requests/new", icon: FilePlus, label: "New Request" },
  { to: "/facility/proposals", icon: FileCheck, label: "Proposals" },
  { to: "/facility/assignments", icon: Users, label: "Assignments" },
  { to: "/facility/invoices", icon: Receipt, label: "Invoices" },
  { to: "/facility/incidents", icon: AlertTriangle, label: "Incidents" },
];

export default function FacilityLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-nav sticky top-0 z-40 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/facility"><NetstaffLogo className="h-9 w-9" textClass="text-xl" /></Link>
          <div className="flex items-center gap-3">
            <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="facility-notif-btn">
              <Bell className="w-5 h-5 text-slate-600" />
              {notifs.filter((n) => !n.is_read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
              )}
            </button>
            <div className="text-sm text-right">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">Facility account</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="facility-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8 grid md:grid-cols-12 gap-6">
        <aside className="md:col-span-3 lg:col-span-2">
          <nav className="space-y-1 sticky top-24">
            {nav.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`facility-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "bg-brand-navy text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>
        <main className="md:col-span-9 lg:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
